import { ArrowUp } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { Link, useNavigate, useParams } from "react-router-dom";

import LayoutAction from "../../actions/LayoutAction";
import { useGtmBrainQuery } from "../hooks/useGtmBrain";
import { useGtmPlayCreateMutation, useGtmPlaysQuery } from "../hooks/useGtmPlays";
import PlaySuggestionsPanel from "../plays/PlaySuggestionsPanel";
import {
  buildPlaySuggestions,
  type PlaySuggestion,
} from "../plays/playSuggestions";

export default function GtmNewPlayPage() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { projectId = "" } = useParams<{ projectId: string }>();

  const [audience, setAudience] = useState("");
  const [usingSuggestionId, setUsingSuggestionId] = useState<string | null>(null);

  const playsQuery = useGtmPlaysQuery(projectId);
  const brainQuery = useGtmBrainQuery(projectId);
  const createMutation = useGtmPlayCreateMutation(projectId);

  const plays = playsQuery.data || [];
  const brain = brainQuery.data?.brain ?? null;
  const docs = brainQuery.data?.docs || [];
  const hasBrain = !!brain;
  const loading = playsQuery.isLoading || brainQuery.isLoading;
  const creating = createMutation.isPending;

  const suggestions = useMemo(
    () => buildPlaySuggestions({ brain, docs, plays }),
    [brain, docs, plays]
  );

  const playsHref = `/workspaces/${projectId}/gtm/leads?key=Leads`;
  const brainHref = `/workspaces/${projectId}/gtm/leads/brain?key=Leads`;
  const canSubmit = audience.trim().length > 0 && !creating;

  useEffect(() => {
    document.title = "GTM · New play";
  }, []);

  useEffect(() => {
    if (brainQuery.isLoading) return;
    if (!hasBrain) {
      dispatch(
        LayoutAction.setNotification({
          title: t("New play"),
          type: "error",
          message: t("Build a GTM brain before creating a play"),
        }) as any
      );
      navigate(brainHref, { replace: true });
    }
  }, [brainHref, brainQuery.isLoading, dispatch, hasBrain, navigate, t]);

  const createPlay = async (
    body: { audience: string; name?: string },
    suggestionId?: string
  ) => {
    const trimmed = body.audience.trim();
    if (!trimmed) {
      dispatch(
        LayoutAction.setNotification({
          title: t("New play"),
          type: "error",
          message: t("Describe the audience for this play"),
        }) as any
      );
      return;
    }
    try {
      if (suggestionId) setUsingSuggestionId(suggestionId);
      const play = await createMutation.mutateAsync({
        audience: trimmed,
        name: body.name?.trim() || undefined,
      });
      dispatch(
        LayoutAction.setNotification({
          title: t("Plays"),
          type: "success",
          message: play.angle
            ? t("Play created")
            : t("Play created (angle pending — you can edit it later)"),
        }) as any
      );
      navigate(`/workspaces/${projectId}/gtm/leads/plays/${play.id}?key=Leads`);
    } catch (err: any) {
      dispatch(
        LayoutAction.setNotification({
          title: t("New play"),
          type: "error",
          message: err?.response?.data?.message || err?.message || t("Failed to create play"),
        }) as any
      );
    } finally {
      setUsingSuggestionId(null);
    }
  };

  const onSubmit = () => {
    if (!canSubmit) return;
    void createPlay({ audience });
  };

  const onUseSuggestion = (suggestion: PlaySuggestion) => {
    void createPlay(
      { audience: suggestion.audience, name: suggestion.name },
      suggestion.id
    );
  };

  return (
    <div className="flex h-full flex-col">
      <div className="border-b border-slate-100 px-6 py-3 text-xs text-slate-500">
        <Link to={playsHref} className="hover:text-slate-800 hover:underline">
          {t("Plays")}
        </Link>
        <span className="mx-1.5 text-slate-300">/</span>
        <span className="font-medium text-slate-800">{t("New play")}</span>
      </div>

      <div className="min-h-0 flex-1 overflow-auto px-6 py-8">
        <div className="mx-auto w-full max-w-4xl space-y-10">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
              {t("What should your new play find?")}
            </h1>
            <p className="mt-2 max-w-2xl text-sm leading-relaxed text-slate-500">
              {t(
                "Describe the people you want to reach. We’ll ask what we still need, then show the play here for you to create."
              )}
            </p>

            <div className="relative mt-6">
              <textarea
                value={audience}
                onChange={(e) => setAudience(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                    e.preventDefault();
                    onSubmit();
                  }
                }}
                rows={4}
                disabled={creating || loading}
                placeholder={t(
                  "Who should this play find? Heads of sales at B2B SaaS companies in France, for example."
                )}
                className="w-full resize-y rounded-2xl border border-slate-200 bg-white px-4 py-4 pr-14 text-sm leading-relaxed text-slate-900 shadow-sm placeholder:text-slate-400 focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-200 disabled:opacity-60"
              />
              <button
                type="button"
                onClick={onSubmit}
                disabled={!canSubmit}
                aria-label={t("Create play")}
                className="absolute bottom-3 right-3 flex h-9 w-9 items-center justify-center rounded-full bg-violet-600 text-white shadow-sm transition-colors hover:bg-violet-700 disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-400"
              >
                <ArrowUp className="h-4 w-4" strokeWidth={2.5} />
              </button>
            </div>
            <p className="mt-2 text-[11px] text-slate-400">
              {t("Press ⌘ Enter to create")}
            </p>
          </div>

          {loading ? (
            <p className="text-sm text-slate-500">{t("Loading suggestions…")}</p>
          ) : suggestions.length > 0 ? (
            <PlaySuggestionsPanel
              suggestions={suggestions}
              usingId={usingSuggestionId}
              onUse={onUseSuggestion}
            />
          ) : (
            <p className="text-sm text-slate-500">
              {t(
                "No suggestions yet. Add personas or company signals in Brain, or describe an audience above."
              )}{" "}
              <Link to={brainHref} className="font-medium text-violet-600 hover:underline">
                {t("Go to Brain")}
              </Link>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
